/**
 * Relative luminance of a hex color (0–1). Used to pick black or white text for contrast.
 * @see https://www.w3.org/TR/WCAG20-TECHS/G17.html
 */
function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const [rs, gs, bs] = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  ) as [number, number, number];
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns black or white for text on the given background hex for readable contrast.
 */
export function getContrastTextColor(hex: string): "#000" | "#fff" {
  return luminance(hex) > 0.5 ? "#000" : "#fff";
}

const bodyBg = "#000000";
const text = "#ffffff";
const highlight = "#ffffff";
const highlightContrast = getContrastTextColor(highlight);

const navAccent = highlight;
const navAccentText = highlightContrast;

/** Hub palette: black stage, white chrome/type (matches title-slide comps). */
export const staticColors = {
  bodyBg,
  text,
  textMuted: "#888888",
  link: text,
  linkHover: text,
  linkHoverUnderline: highlight,
  border: "rgba(255, 255, 255, 1)",
  gridLine: "rgba(255, 255, 255, 1)",
  navBg: bodyBg,
  navFg: text,
  navShadow: "transparent",
  navAccent,
  navAccentText,
  footerBg: bodyBg,
  footerText: text,
  footerLink: text,
  footerBorder: "rgba(255, 255, 255, 0.35)",
  footerAccent: highlight,
  footerIconFill: text,
  footerIconHoverFill: highlightContrast,
  ledeMetaAccent: highlight,
  ledeMetaAccentContrast: highlightContrast,
  placeholderBg: "#1a1a1a",
  dekBg: "#ffffff",
  dekFg: "#000000",
  endmarkFill: text,
  endmarkStroke: text,
} as const;
